import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import DashboardStats from './components/DashboardStats';
import ActiveProjectCard from './components/ActiveProjectCard';
import ServiceRequestCard from './components/ServiceRequestCard';
import RecentActivityItem from './components/RecentActivityItem';
import DocumentCard from './components/DocumentCard';
import UpcomingMeetingCard from './components/UpcomingMeetingCard';
import QuickActionCard from './components/QuickActionCard';
import NotificationItem from './components/NotificationItem';

import axios from 'axios';

const ClientPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('overview');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [filterService, setFilterService] = React.useState('all');
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [user, setUser] = React.useState(null);
  const [projectsData, setProjectsData] = React.useState([]);
  const [requestsData, setRequestsData] = React.useState([]);
  const [activitiesData, setActivitiesData] = React.useState([]);
  const [documentsData, setDocumentsData] = React.useState([]);
  const [meetingsData, setMeetingsData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost/isplw/api';

  const fetchAllData = async (clientId) => {
    try {
      setLoading(true);
      const [projectsResp, requestsResp, activitiesResp, docsResp, meetingsResp] = await Promise.all([
        axios.get(`${API_URL}/projects.php`, { params: { client_id: clientId } }),
        axios.get(`${API_URL}/service_requests.php`, { params: { client_id: clientId } }),
        axios.get(`${API_URL}/activity.php`, { params: { client_id: clientId } }),
        axios.get(`${API_URL}/documents.php`, { params: { client_id: clientId } }),
        axios.get(`${API_URL}/meetings.php`, { params: { client_id: clientId } })
      ]);

      if (projectsResp.data.status === 'success') {
        setProjectsData(projectsResp.data.data.map(p => ({
          ...p,
          id: parseInt(p.id),
          progress: parseInt(p.progress),
          type: "Custom Solution",
          priority: "Medium",
          deadline: "TBD",
          icon: "Briefcase"
        })));
      }
      if (requestsResp.data.status === 'success') {
        setRequestsData(requestsResp.data.data.map(r => ({
          ...r,
          id: parseInt(r.id),
          title: r.service_name,
          serviceType: r.category,
          submittedDate: new Date(r.created_at).toLocaleDateString(),
          urgency: "Normal",
          serviceIcon: r.icon_name || "FileText"
        })));
      }
      if (activitiesResp.data.status === 'success') {
        setActivitiesData(activitiesResp.data.data.map(a => ({
          ...a,
          id: parseInt(a.id),
          type: "project_update",
          timestamp: new Date(a.created_at),
          user: { name: "System", avatar: "/assets/images/no_image.png" }
        })));
      }
      if (docsResp.data.status === 'success') {
        setDocumentsData(docsResp.data.data.map(d => ({
          ...d,
          id: parseInt(d.id),
          uploadDate: new Date(d.created_at).toLocaleDateString(),
          category: "General"
        })));
      }
      if (meetingsResp.data.status === 'success') {
        setMeetingsData(meetingsResp.data.data.map(m => ({
          ...m,
          id: parseInt(m.id),
          type: "Video Call",
          location: "Online",
          participants: []
        })));
      }
    } catch (err) {
      console.error("Failed to fetch client data", err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      fetchAllData(userData.id);
    } else {
      navigate('/login');
    }
  }, []);

  const dashboardStats = [
    {
      id: 1,
      label: "Active Projects",
      value: projectsData.filter(p => p.status === 'In Progress').length.toString(),
      subtitle: "Nearing completion",
      icon: "FolderOpen",
      iconColor: "var(--color-primary)",
      bgColor: "bg-primary/10",
      trend: { direction: "up", value: "+0" }
    },
    {
      id: 2,
      label: "Service Requests",
      value: requestsData.length.toString(),
      subtitle: "Pending approval",
      icon: "FileText",
      iconColor: "var(--color-accent)",
      bgColor: "bg-accent/10",
      trend: { direction: "up", value: "+0" }
    },
    {
      id: 3,
      label: "Team Members",
      value: "45",
      subtitle: "Across all projects",
      icon: "Users",
      iconColor: "var(--color-success)",
      bgColor: "bg-success/10",
      trend: { direction: "up", value: "+5" }
    },
    {
      id: 4,
      label: "Documents",
      value: documentsData.length.toString(),
      subtitle: "Available now",
      icon: "File",
      iconColor: "var(--color-secondary)",
      bgColor: "bg-secondary/10",
      trend: { direction: "up", value: "+0" }
    }];


  const quickActions = [
    {
      id: 1,
      title: "New Service Request",
      description: "Submit a new service request for HR, Trade, or Engineering services",
      icon: "Plus",
      iconColor: "var(--color-primary)",
      bgColor: "bg-primary/10"
    },
    {
      id: 2,
      title: "Schedule Meeting",
      description: "Book a consultation with our expert team members",
      icon: "Calendar",
      iconColor: "var(--color-accent)",
      bgColor: "bg-accent/10"
    },
    {
      id: 3,
      title: "Upload Documents",
      description: "Share project files, contracts, or other important documents",
      icon: "Upload",
      iconColor: "var(--color-success)",
      bgColor: "bg-success/10"
    },
    {
      id: 4,
      title: "View Reports",
      description: "Access detailed analytics and performance reports",
      icon: "BarChart3",
      iconColor: "var(--color-secondary)",
      bgColor: "bg-secondary/10"
    }];


  useEffect(() => {
    const mockNotifications = [
      {
        id: 1,
        type: "success",
        title: "Project Milestone Completed",
        message: "Enterprise HR Management System has reached 68% completion.",
        timestamp: new Date(Date.now() - 900000),
        isRead: false
      },
      {
        id: 2,
        type: "info",
        title: "New Document Available",
        message: "Quality audit report has been uploaded to your document library.",
        timestamp: new Date(Date.now() - 3600000),
        isRead: false
      },
      {
        id: 3,
        type: "warning",
        title: "Upcoming Deadline",
        message: "Enterprise HR Management System deadline is approaching in 15 days.",
        timestamp: new Date(Date.now() - 7200000),
        isRead: true
      }];

    setNotifications(mockNotifications);
  }, []);

  const handleMarkAsRead = (notificationId) => {
    setNotifications((prev) =>
      prev?.map((notif) =>
        notif?.id === notificationId ? { ...notif, isRead: true } : notif
      )
    );
  };

  const unreadCount = notifications?.filter((n) => !n?.isRead)?.length;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'projects', label: 'Projects', icon: 'FolderOpen' },
    { id: 'requests', label: 'Service Requests', icon: 'FileText' },
    { id: 'documents', label: 'Documents', icon: 'File' },
    { id: 'meetings', label: 'Meetings', icon: 'Video' }];


  const serviceOptions = [
    { value: 'all', label: 'All Services' },
    { value: 'hr', label: 'HR Solutions' },
    { value: 'trade', label: 'Trade Network' },
    { value: 'engineering', label: 'Engineering Services' }];


  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 pt-16">
        <div className="bg-gradient-to-br from-primary via-primary/95 to-secondary text-white py-8 md:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex items-center gap-4 md:gap-6">
                <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-full border-4 border-white/20 bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={40} className="text-white/60" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">
                    Welcome back, {user?.name || 'Partner'}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm md:text-base text-white/80">
                    <div className="flex items-center gap-2">
                      <Icon name="Building2" size={16} />
                      <span>{user?.company_name || 'Valued Client'}</span>
                    </div>
                    <span className="hidden md:inline">•</span>
                    <div className="flex items-center gap-2">
                      <Icon name="Award" size={16} />
                      <span>Enterprise Member</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 w-full lg:w-auto">
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative bg-white/10 hover:bg-white/20 text-white"
                    onClick={() => setShowNotifications(!showNotifications)}>

                    <Icon name="Bell" size={20} />
                    {unreadCount > 0 &&
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-error rounded-full flex items-center justify-center text-xs font-bold">
                        {unreadCount}
                      </span>
                    }
                  </Button>

                  {showNotifications &&
                    <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-card rounded-lg shadow-xl border border-border z-50 max-h-96 overflow-y-auto">
                      <div className="p-4 border-b border-border sticky top-0 bg-card">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-foreground">Notifications</h3>
                          <button
                            onClick={() => setShowNotifications(false)}
                            className="text-muted-foreground hover:text-foreground">

                            <Icon name="X" size={18} />
                          </button>
                        </div>
                      </div>
                      <div className="divide-y divide-border">
                        {notifications?.map((notification) =>
                          <NotificationItem
                            key={notification?.id}
                            notification={notification}
                            onMarkAsRead={handleMarkAsRead} />

                        )}
                      </div>
                    </div>
                  }
                </div>

                <Button
                  variant="default"
                  className="bg-white text-primary hover:bg-white/90"
                  iconName="Settings"
                  iconPosition="left">

                  <span className="hidden md:inline">Account Settings</span>
                  <span className="md:hidden">Settings</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 lg:py-12">
          <DashboardStats stats={dashboardStats} />

          <div className="mt-8 md:mt-12">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto">
                {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${activeTab === tab?.id ?
                      'bg-primary text-white' : 'bg-card text-muted-foreground hover:bg-muted'}`
                    }>

                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                )}
              </div>

              <div className="flex gap-3 w-full md:w-auto">
                <Input
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="flex-1 md:w-64" />

                <Select
                  options={serviceOptions}
                  value={filterService}
                  onChange={setFilterService}
                  className="w-40" />

              </div>
            </div>

            {activeTab === 'overview' &&
              <div className="space-y-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">
                    Quick Actions
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {quickActions?.map((action) =>
                      <QuickActionCard
                        key={action?.id}
                        action={action}
                        onClick={() => console.log(`Action: ${action?.title}`)} />

                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">
                      Active Projects
                    </h2>
                    <div className="space-y-4">
                      {loading ? (
                        <div className="p-12 text-center bg-card rounded-lg border border-dashed border-border flex flex-col items-center">
                          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
                          <p className="text-muted-foreground">Loading your projects...</p>
                        </div>
                      ) : projectsData.length > 0 ? (
                        projectsData.slice(0, 2).map((project) => (
                          <ActiveProjectCard key={project.id} project={project} />
                        ))
                      ) : (
                        <div className="p-8 text-center bg-card rounded-lg border border-dashed border-border">
                          <p className="text-muted-foreground">No active projects found.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">
                      Recent Activity
                    </h2>
                    <div className="bg-card rounded-lg border border-border divide-y divide-border">
                      {loading ? (
                        <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>
                      ) : activitiesData.length > 0 ? (
                        activitiesData.slice(0, 4).map((activity) => (
                          <RecentActivityItem key={activity.id} activity={activity} />
                        ))
                      ) : (
                        <p className="p-8 text-center text-muted-foreground text-sm">No recent activity.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">
                      Upcoming Meetings
                    </h2>
                    <div className="space-y-4">
                      {loading ? (
                        <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>
                      ) : meetingsData.length > 0 ? (
                        meetingsData.slice(0, 2).map((meeting) => (
                          <UpcomingMeetingCard key={meeting.id} meeting={meeting} />
                        ))
                      ) : (
                        <div className="p-8 text-center bg-card rounded-lg border border-dashed border-border"><p className="text-muted-foreground text-sm">No upcoming meetings.</p></div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">
                      Recent Documents
                    </h2>
                    <div className="grid grid-cols-1 gap-4">
                      {loading ? (
                        <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>
                      ) : documentsData.length > 0 ? (
                        documentsData.slice(0, 2).map((document) => (
                          <DocumentCard key={document.id} document={document} />
                        ))
                      ) : (
                        <div className="p-8 text-center bg-card rounded-lg border border-dashed border-border"><p className="text-muted-foreground text-sm">No recent documents.</p></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            }

            {activeTab === 'projects' &&
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">
                    All Active Projects
                  </h2>
                  <Button variant="default" iconName="Plus" iconPosition="left">
                    New Project
                  </Button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {loading ? (
                    <div className="col-span-full p-12 text-center">
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    </div>
                  ) : projectsData.length > 0 ? (
                    projectsData.map((project) => (
                      <ActiveProjectCard key={project.id} project={project} />
                    ))
                  ) : (
                    <div className="col-span-full p-12 text-center bg-card rounded-lg border border-dashed border-border">
                      <p className="text-muted-foreground">No projects found.</p>
                    </div>
                  )}
                </div>
              </div>
            }

            {activeTab === 'requests' &&
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">
                    Service Requests
                  </h2>
                  <Button variant="default" iconName="Plus" iconPosition="left">
                    New Request
                  </Button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {loading ? (
                    <div className="col-span-full p-12 text-center">
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    </div>
                  ) : requestsData.length > 0 ? (
                    requestsData.map((request) => (
                      <ServiceRequestCard key={request.id} request={request} />
                    ))
                  ) : (
                    <div className="col-span-full p-12 text-center bg-card rounded-lg border border-dashed border-border">
                      <p className="text-muted-foreground">No service requests found.</p>
                    </div>
                  )}
                </div>
              </div>
            }

            {activeTab === 'documents' &&
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">
                    Document Library
                  </h2>
                  <Button variant="default" iconName="Upload" iconPosition="left">
                    Upload Document
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {loading ? (
                    <div className="col-span-full p-12 text-center">
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    </div>
                  ) : documentsData.length > 0 ? (
                    documentsData.map((document) => (
                      <DocumentCard key={document.id} document={document} />
                    ))
                  ) : (
                    <div className="col-span-full p-12 text-center bg-card rounded-lg border border-dashed border-border">
                      <p className="text-muted-foreground">No documents found.</p>
                    </div>
                  )}
                </div>
              </div>
            }

            {activeTab === 'meetings' &&
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">
                    Upcoming Meetings
                  </h2>
                  <Button variant="default" iconName="Calendar" iconPosition="left">
                    Schedule Meeting
                  </Button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {loading ? (
                    <div className="col-span-full p-12 text-center">
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    </div>
                  ) : meetingsData.length > 0 ? (
                    meetingsData.map((meeting) => (
                      <UpcomingMeetingCard key={meeting.id} meeting={meeting} />
                    ))
                  ) : (
                    <div className="col-span-full p-12 text-center bg-card rounded-lg border border-dashed border-border">
                      <p className="text-muted-foreground">No upcoming meetings.</p>
                    </div>
                  )}
                </div>
              </div>
            }
          </div>
        </div>
      </main>
      <Footer />
    </div>);

};

export default ClientPortal;